// =============================================================================
// ExceptionRow - Riga riusabile per un'assenza / presenza aggiuntiva
// =============================================================================

import { format, parseISO } from 'date-fns';
import { Trash2, Edit2, CalendarPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDateLocale } from '../../hooks/useDateLocale';
import { ResourceException } from '../../api/hooks';
import { getReasonVisual, hexToRgba } from '../scheduler/utils/exceptionStyle';

interface ExceptionRowProps {
  exception: ResourceException;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
  /** Etichetta risorsa (mostrata nella vista centralizzata). */
  resourceLabel?: string;
  /** Colore risorsa per il pallino identificativo. */
  resourceColor?: string | null;
}

export function ExceptionRow({
  exception,
  onEdit,
  onDelete,
  deleting,
  resourceLabel,
  resourceColor,
}: ExceptionRowProps) {
  const { t } = useTranslation('settings');
  const locale = useDateLocale();

  const start = parseISO(exception.startDate.slice(0, 10));
  const end = parseISO(exception.endDate.slice(0, 10));
  const isSingleDay = exception.startDate.slice(0, 10) === exception.endDate.slice(0, 10);
  const dateLabel = isSingleDay
    ? format(start, 'd MMM yyyy', { locale })
    : `${format(start, 'd MMM', { locale })} → ${format(end, 'd MMM yyyy', { locale })}`;

  const timeLabel =
    exception.startTime && exception.endTime
      ? `${exception.startTime} – ${exception.endTime}`
      : t('exceptions.allDay');

  // Motivo (causale) → colore/icona; etichetta = titolo o nome causale.
  const visual = getReasonVisual(exception.reason);
  const AbsenceIcon = visual.Icon;
  const label =
    exception.title || exception.reason?.name || t('exceptions.absence');
  const reasonChip = exception.reason?.name ?? null;

  return (
    <li
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        exception.isAvailable
          ? 'border-emerald-200 bg-emerald-50/40'
          : 'border-red-100 bg-red-50/40'
      }`}
    >
      {exception.isAvailable ? (
        <div className="p-2 rounded-lg flex-shrink-0 bg-emerald-100 text-emerald-600">
          <CalendarPlus className="w-4 h-4" />
        </div>
      ) : (
        <div
          className="p-2 rounded-lg flex-shrink-0"
          style={{ backgroundColor: hexToRgba(visual.color, 0.12), color: visual.color }}
        >
          <AbsenceIcon className="w-4 h-4" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {resourceLabel && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-1.5 py-0.5 bg-gray-100 rounded">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: resourceColor || '#6B7280' }}
              />
              {resourceLabel}
            </span>
          )}
          <p className="font-medium text-gray-900 truncate">{label}</p>
          {!exception.isAvailable && reasonChip && (
            <span
              className="text-xs px-1.5 py-0.5 rounded"
              style={{ backgroundColor: hexToRgba(visual.color, 0.12), color: visual.color }}
            >
              {reasonChip}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 truncate">
          {dateLabel} · {timeLabel}
          {exception.isAvailable && (
            <span className="ml-1 text-emerald-600 font-medium">· {t('exceptions.extra')}</span>
          )}
        </p>
      </div>

      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          title={t('common:buttons.edit')}
        >
          <Edit2 className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          disabled={deleting}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          title={t('common:buttons.delete')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}
